import { useEffect, useRef } from "react";
import type { ShortcutEntry } from "../page-shortcuts.tsx";

/** The words of "Shift + J / K", each keyed by the text up to it: unique within the keys, and stable. */
function keyWords(keys: string): { text: string; id: string }[] {
  const words = keys.split(" ");
  return words.map((text, at) => ({ text, id: words.slice(0, at + 1).join(" ") }));
}

/**
 * The words that are no key of their own: what joins keys ("Shift + J", "J / K", "1 to 8", "G then
 * I"), and the mouse's part in "Shift + click", drawn as words beside the caps.
 */
const JOINS = new Set(["+", "/", "to", "then", "click"]);

/** "Shift + J / K" drawn as keycaps: each key its own cap, the joining words between. */
function Keys(props: { keys: string }) {
  const words = keyWords(props.keys);
  if (words.length === 1) return <kbd className="lcr-kbd">{props.keys}</kbd>;
  return (
    <span className="lcr-key-combo">
      {words.map((word) =>
        JOINS.has(word.text) ? (
          <span key={word.id} className="lcr-key-join">
            {word.text}
          </span>
        ) : (
          <kbd key={word.id} className="lcr-kbd">
            {word.text}
          </kbd>
        ),
      )}
    </span>
  );
}

/** The entries in runs of one group, in the order the page listed them; ungrouped ones run together. */
function grouped(entries: readonly ShortcutEntry[]): { name: string; entries: ShortcutEntry[] }[] {
  const groups: { name: string; entries: ShortcutEntry[] }[] = [];
  for (const entry of entries) {
    const name = entry.group ?? "";
    const last = groups.at(-1);
    if (last?.name === name) last.entries.push(entry);
    else groups.push({ name, entries: [entry] });
  }
  return groups;
}

/*
 * The keyboard-shortcuts panel. It takes focus when it opens, so a screen reader lands in it and
 * Escape reaches it first; a click anywhere else closes it; and focus goes back to the account
 * control it was opened from, because the menu item that opened it no longer exists (after-critique
 * of 2026-09-22: it had no mouse exit and dropped focus to the page). It lists the keys of the page
 * on screen, which that page registers (`page-shortcuts.tsx`).
 */
export function ShortcutsPanel({
  onClose,
  entries,
}: {
  onClose: () => void;
  entries: readonly ShortcutEntry[];
}) {
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const opener = document.activeElement;
    panel.current?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && panel.current?.contains(event.target)) return;
      close.current();
    };
    document.addEventListener("pointerdown", outside);
    return () => {
      document.removeEventListener("pointerdown", outside);
      // Where it came from: the element that had the focus, or the page itself where nothing had it
      // (a key pressed on the page). Only an element that has since gone hands it to the account
      // button.
      if (!(opener instanceof HTMLElement)) return;
      const back = opener.isConnected
        ? opener
        : document.querySelector<HTMLElement>(".lcr-rail-account-button");
      back?.focus();
    };
  }, []);
  return (
    <div
      ref={panel}
      className="lcr-popover lcr-shortcuts-panel"
      role="dialog"
      aria-label="Keyboard shortcuts"
      tabIndex={-1}
    >
      <p className="lcr-label">Keyboard shortcuts</p>
      <div className="lcr-shortcuts-groups">
        {grouped(entries).map((group) => (
          <dl
            key={`${group.name}:${group.entries[0]?.keys}`}
            className="lcr-shortcuts-group"
            aria-label={group.name || undefined}
          >
            {group.entries.map((entry) => (
              <div key={entry.keys}>
                <dt>
                  <Keys keys={entry.keys} />
                </dt>
                <dd>{entry.does}</dd>
              </div>
            ))}
          </dl>
        ))}
      </div>
    </div>
  );
}
